// eslint-disable-next-line @altermaind/you-shall-not-pass
import { Dispatch, FC, SetStateAction, useCallback, useEffect } from 'react';
import dayjs from 'dayjs';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Input, Svg } from '@altermaind/web/ui';
import { usePaginatedTable, PaginatedTable, Column } from '@/_shared/components';
import { getLocaleAndFormatDateFromTimeZone } from '@/_shared/helpers/getLocaleAndFormatDateFromTimeZone';
import { useNotification } from '@/_shared/hooks';
import {
  ImportedEmployer,
  getEmployeesImportCall,
  postEmployeesImportsTemplateCall,
  putEmployeesImportCall,
} from '../../../../state';

type Props = {
  setSelectedProfile: (profile: ImportedEmployer | undefined) => void;
  selectedProfile: ImportedEmployer | undefined;
  paginedProps: ReturnType<typeof usePaginatedTable>;
  setDisplayedData: Dispatch<SetStateAction<ImportedEmployer[]>>;
  pageSize: number;
};

export const TableImportedProfiles: FC<Props> = ({
  setSelectedProfile,
  selectedProfile,
  paginedProps,
  setDisplayedData,
  pageSize,
}) => {
  const t = useTranslations('people.importCompanyData.verifyData.editImportedProfiles.table');
  const tShared = useTranslations();
  const { notify } = useNotification();
  const { dateFormat } = getLocaleAndFormatDateFromTimeZone();
  const { jobId } = postEmployeesImportsTemplateCall.hook();
  const { loading: loadingGetEmployeesImport } = getEmployeesImportCall.hook();
  const { execute: executePutEmployeesImport } = putEmployeesImportCall.hook();

  const updateEmployee = useCallback(
    (employee: ImportedEmployer) => {
      if (jobId && employee.id) {
        const oldPaginatedData = { ...paginedProps.paginatedData } as ImportedEmployer[];
        setDisplayedData(
          (paginedProps.paginatedData as ImportedEmployer[]).map(emp =>
            emp.id === employee.id ? employee : emp,
          ),
        );
        executePutEmployeesImport({
          jobId,
          recordId: employee.id || '',
          ...employee,
          successSaga: ({ response }) => {
            setDisplayedData(
              (paginedProps.paginatedData as ImportedEmployer[]).map(emp =>
                emp.id === employee.id ? response : emp,
              ),
            );
          },
          errorSaga: () => {
            setDisplayedData(oldPaginatedData);
            notify(tShared('errors.genericApiError'), 'error');
          },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [executePutEmployeesImport, jobId, paginedProps.paginatedData, setDisplayedData, tShared],
  );

  const columns: Column[] = [
    {
      key: 'status',
      title: '',
      renderValue: (item: ImportedEmployer) => {
        const isError = item.issues?.length;
        return (
          <Box display='flex' alignItems='center'>
            <Box
              mr='xxs'
              size='m'
              borderRadius='100%'
              bg={isError ? 'error' : 'primary/interactive'}
              display='flex'
              justifyContent='center'
              alignItems='center'
            >
              <Svg name={isError ? 'close' : 'check'} size='s' />
            </Box>
          </Box>
        );
      },
      fitContent: true,
    },
    {
      key: 'name',
      title: t('name'),
      renderValue: (item: ImportedEmployer) => (
        <Box display='flex' alignItems='center' ml='-xs'>
          <Input
            key={`firstName-${item.id}`}
            name={`firstName-${item.id}`}
            type='text'
            placeholder='-'
            defaultValue={item.firstName || ''}
            onBlur={({ target: { value } }) => {
              if (item.firstName !== value) {
                updateEmployee({ ...item, firstName: value });
              }
            }}
            backgroundColor='transparent'
            color={item.issues.find(issue => issue.fieldName === 'firstName') ? 'error' : undefined}
          />
        </Box>
      ),
      fitContent: true,
      columnProps: {
        minWidth: 150,
      },
    },
    {
      key: 'surname',
      title: t('surname'),
      renderValue: (item: ImportedEmployer) => (
        <Box display='flex' alignItems='center' ml='-xs'>
          <Input
            key={`lastName-${item.id}`}
            name={`lastName-${item.id}`}
            type='text'
            placeholder='-'
            defaultValue={item.lastName || ''}
            onBlur={({ target: { value } }) => {
              if (item.lastName !== value) {
                updateEmployee({ ...item, lastName: value });
              }
            }}
            backgroundColor='transparent'
            color={item.issues.find(issue => issue.fieldName === 'lastName') ? 'error' : undefined}
          />
        </Box>
      ),
      fitContent: true,
      columnProps: {
        minWidth: 150,
      },
    },
    {
      key: 'email',
      title: t('email'),
      renderValue: (item: ImportedEmployer) => (
        <Box display='flex' alignItems='center' ml='-xs'>
          <Input
            key={`email-${item.id}`}
            name={`email-${item.id}`}
            type='text'
            placeholder='-'
            defaultValue={item.email || ''}
            onBlur={({ target: { value } }) => {
              if (item.email !== value) {
                updateEmployee({ ...item, email: value });
              }
            }}
            backgroundColor='transparent'
            color={item.issues.find(issue => issue.fieldName === 'email') ? 'error' : undefined}
          />
        </Box>
      ),
      fitContent: true,
      columnProps: {
        minWidth: 200,
      },
    },
    {
      key: 'birthDate',
      title: t('birthDate'),
      renderValue: (item: ImportedEmployer) => (
        <Box display='flex' alignItems='center' ml='-xs'>
          <Input
            key={`birthDate-${item.id}`}
            name={`birthDate-${item.id}`}
            type='text'
            placeholder='DD/MM/YYYY'
            defaultValue={item?.birthDate ? dayjs(item.birthDate).format(dateFormat) : ''}
            onBlur={({ target: { value } }) => {
              updateEmployee({ ...item, birthDate: value });
            }}
            backgroundColor='transparent'
            color={
              item.issues.find(issue => {
                return issue.fieldName === 'birthDate';
              })
                ? 'error'
                : undefined
            }
          />
        </Box>
      ),
      fitContent: true,
      columnProps: {
        minWidth: 120,
      },
    },
    {
      key: 'role',
      title: t('role'),
      renderValue: (item: ImportedEmployer) => (
        <Box display='flex' alignItems='center' ml='-xs'>
          <Input
            key={`role-${item.id}`}
            name={`role-${item.id}`}
            type='text'
            placeholder='-'
            defaultValue={item.role || ''}
            onBlur={({ target: { value } }) => {
              if (item.role !== value) {
                updateEmployee({ ...item, role: value });
              }
            }}
            backgroundColor='transparent'
            color={item.issues.find(issue => issue.fieldName === 'role') ? 'error' : undefined}
          />
        </Box>
      ),
      fitContent: true,
      columnProps: {
        minWidth: 180,
      },
    },
    {
      key: 'department',
      title: t('department'),
      renderValue: (item: ImportedEmployer) => (
        <Box display='flex' alignItems='center' ml='-xs'>
          <Input
            key={`department-${item.id}`}
            name={`department-${item.id}`}
            type='text'
            placeholder='-'
            defaultValue={item.department?.toLowerCase() === 'unknown' ? '' : item.department || ''}
            onBlur={({ target: { value } }) => {
              if (item.department !== value) {
                updateEmployee({ ...item, department: value });
              }
            }}
            backgroundColor='transparent'
            color={
              item.issues.find(issue => issue.fieldName === 'department') ? 'error' : undefined
            }
          />
        </Box>
      ),
      fitContent: true,
      columnProps: {
        minWidth: 150,
      },
    },
    {
      key: 'manager',
      title: t('manager'),
      renderValue: (item: ImportedEmployer) => (
        <Box display='flex' alignItems='center' ml='-xs'>
          <Input
            key={`emailManager-${item.id}`}
            name={`emailManager-${item.id}`}
            type='text'
            placeholder='-'
            defaultValue={item.emailManager || ''}
            onBlur={({ target: { value } }) => {
              if (item.emailManager !== value) {
                updateEmployee({ ...item, emailManager: value });
              }
            }}
            backgroundColor='transparent'
            color={
              item.issues.find(issue => issue.fieldName === 'emailManager') ? 'error' : undefined
            }
          />
        </Box>
      ),
      fitContent: true,
      columnProps: {
        minWidth: 180,
      },
    },
    {
      key: 'area',
      title: t('area'),
      renderValue: (item: ImportedEmployer) => (
        <Box display='flex' alignItems='center' ml='-xs'>
          <Input
            key={`area-${item.id}`}
            name={`area-${item.id}`}
            type='text'
            placeholder='-'
            defaultValue={item.area?.toLowerCase() === 'unknown' ? '' : item.area || ''}
            onBlur={({ target: { value } }) => {
              if (item.area !== value) {
                updateEmployee({ ...item, area: value });
              }
            }}
            backgroundColor='transparent'
            color={item.issues.find(issue => issue.fieldName === 'area') ? 'error' : undefined}
          />
        </Box>
      ),
      fitContent: true,
    },
    {
      key: 'fiscalCode',
      title: t('fiscalCode'),
      renderValue: (item: ImportedEmployer) => (
        <Box display='flex' alignItems='center' ml='-xs'>
          <Input
            key={`fiscalCode-${item.id}`}
            name={`fiscalCode-${item.id}`}
            type='text'
            placeholder='-'
            defaultValue={item.fiscalCode || ''}
            onBlur={({ target: { value } }) => {
              if (item.fiscalCode !== value) {
                updateEmployee({ ...item, fiscalCode: value });
              }
            }}
            backgroundColor='transparent'
            color={
              item.issues.find(issue => issue.fieldName === 'fiscalCode') ? 'error' : undefined
            }
          />
        </Box>
      ),
      fitContent: true,
      columnProps: {
        minWidth: 200,
      },
    },
    {
      key: 'jobLevel',
      title: t('jobLevel'),
      renderValue: (item: ImportedEmployer) => (
        <Box display='flex' alignItems='center' ml='-xs'>
          <Input
            key={`jobLevel-${item.id}`}
            name={`jobLevel-${item.id}`}
            type='text'
            placeholder='-'
            defaultValue={item.jobLevel || ''}
            onBlur={({ target: { value } }) => {
              if (item.jobLevel !== value) {
                updateEmployee({ ...item, jobLevel: value });
              }
            }}
            backgroundColor='transparent'
            color={item.issues.find(issue => issue.fieldName === 'jobLevel') ? 'error' : undefined}
          />
        </Box>
      ),
      fitContent: true,
      columnProps: {
        minWidth: 100,
      },
    },
  ];

  useEffect(() => {
    if (loadingGetEmployeesImport) {
      setSelectedProfile(undefined);
    }
  }, [loadingGetEmployeesImport, setSelectedProfile]);

  return (
    <PaginatedTable
      columns={columns}
      pageSize={pageSize}
      call={{ ...getEmployeesImportCall }}
      onRowClick={(profile: ImportedEmployer) => {
        setSelectedProfile(profile);
      }}
      rowWrapperProps={(profile: ImportedEmployer) => {
        return profile.id === selectedProfile?.id
          ? { backgroundColor: 'grey/dark/background', borderRadius: '10px' }
          : {};
      }}
      paginedProps={paginedProps}
      loading={loadingGetEmployeesImport}
      skeletonProps={{
        hasHeaderRow1: false,
        hasHeaderRow2: false,
        rows:
          !paginedProps?.currentPageNumber || paginedProps?.currentPageNumber <= 1 ? 5 : undefined,
      }}
      hasHover
    />
  );
};
